import { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import { Download, Building2, MapPin, FileSpreadsheet } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ReportsPage = () => {
  const [companies, setCompanies] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCompany, setSelectedCompany] = useState('');
  const [selectedLocation, setSelectedLocation] = useState('');
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [companiesRes, locationsRes] = await Promise.all([
        axios.get(`${API}/companies`),
        axios.get(`${API}/locations`)
      ]);
      // Companies artık pagination formatında geliyor
      setCompanies(companiesRes.data.items || companiesRes.data);
      setLocations(locationsRes.data);
    } catch (error) {
      toast.error('Veriler yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadCompanyReport = async () => {
    if (!selectedCompany) {
      toast.error('Lütfen bir firma seçin');
      return;
    }

    setDownloading(true);
    try {
      const response = await axios.get(`${API}/export/company/${selectedCompany}`, {
        responseType: 'blob'
      });
      
      const company = companies.find(c => c.id === selectedCompany);
      const filename = `${company?.name || 'firma'}_rapor.xlsx`;
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Rapor indirildi');
    } catch (error) {
      toast.error('Rapor indirilemedi');
    } finally {
      setDownloading(false);
    }
  };

  const handleDownloadLocationReport = async () => {
    if (!selectedLocation) {
      toast.error('Lütfen bir lokasyon seçin');
      return;
    }

    setDownloading(true);
    try {
      const response = await axios.get(`${API}/export/location/${selectedLocation}`, {
        responseType: 'blob'
      });
      
      const location = locations.find(l => l.id === selectedLocation);
      const filename = `${location?.name || 'lokasyon'}_rapor.xlsx`;
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Rapor indirildi');
    } catch (error) {
      toast.error('Rapor indirilemedi');
    } finally {
      setDownloading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  return (
    <div data-testid="reports-page" className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-slate-900">Raporlar</h1>
        <p className="text-slate-500 mt-1">Firma ve lokasyon bazlı Excel raporları indirin</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Company Report */}
        <Card className="border-slate-200">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Building2 className="h-5 w-5 text-orange-500" />
              Firma Raporu
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-slate-500">
              Seçilen firmanın tüm personellerini ve belgelerini içeren rapor
            </p>
            <div className="space-y-2">
              <Label>Firma Seçin</Label>
              <Select value={selectedCompany} onValueChange={setSelectedCompany}>
                <SelectTrigger data-testid="report-company-select">
                  <SelectValue placeholder="Firma seçin" />
                </SelectTrigger>
                <SelectContent>
                  {companies.map((company) => (
                    <SelectItem key={company.id} value={company.id}>
                      {company.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button 
              onClick={handleDownloadCompanyReport}
              disabled={!selectedCompany || downloading}
              className="w-full bg-slate-900 hover:bg-slate-800"
              data-testid="download-company-report-btn"
            >
              {downloading ? (
                <span className="flex items-center gap-2">
                  <span className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></span>
                  İndiriliyor...
                </span>
              ) : (
                <>
                  <Download className="h-4 w-4 mr-2" />
                  Excel İndir
                </>
              )}
            </Button>
            <div className="text-xs text-slate-400 space-y-1">
              <p>Rapor içeriği:</p>
              <ul className="list-disc list-inside ml-2">
                <li>Firma bilgileri ve belge durumları</li>
                <li>Personel listesi ve belgeleri</li>
                <li>Çalışma izinleri</li>
              </ul>
            </div>
          </CardContent>
        </Card>

        {/* Location Report */}
        <Card className="border-slate-200">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <MapPin className="h-5 w-5 text-orange-500" />
              Lokasyon Raporu
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-slate-500">
              Seçilen lokasyondaki tüm çalışma izinlerini içeren rapor
            </p>
            <div className="space-y-2">
              <Label>Lokasyon Seçin</Label>
              <Select value={selectedLocation} onValueChange={setSelectedLocation}>
                <SelectTrigger data-testid="report-location-select">
                  <SelectValue placeholder="Lokasyon seçin" />
                </SelectTrigger>
                <SelectContent>
                  {locations.map((location) => (
                    <SelectItem key={location.id} value={location.id}>
                      {location.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button 
              onClick={handleDownloadLocationReport}
              disabled={!selectedLocation || downloading}
              className="w-full bg-slate-900 hover:bg-slate-800"
              data-testid="download-location-report-btn"
            >
              {downloading ? (
                <span className="flex items-center gap-2">
                  <span className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></span>
                  İndiriliyor...
                </span>
              ) : (
                <>
                  <Download className="h-4 w-4 mr-2" />
                  Excel İndir
                </>
              )}
            </Button>
            <div className="text-xs text-slate-400 space-y-1">
              <p>Rapor içeriği:</p>
              <ul className="list-disc list-inside ml-2">
                <li>Lokasyon bilgileri</li>
                <li>Çalışma izinleri ve firmalar</li>
                <li>Personel belge durumları</li>
              </ul>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Info Card */}
      <Card className="border-slate-200 bg-slate-50">
        <CardContent className="p-6">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-xl bg-orange-100 flex items-center justify-center flex-shrink-0">
              <FileSpreadsheet className="h-6 w-6 text-orange-600" />
            </div>
            <div>
              <h3 className="font-semibold text-slate-900">Excel Raporları Hakkında</h3>
              <p className="text-sm text-slate-600 mt-1">
                İndirilen Excel dosyaları birden fazla sayfa içerir. Her sayfa farklı bilgileri 
                (firma bilgileri, personeller, çalışma izinleri) içerir. Raporları filtrelemek 
                veya özelleştirmek için Excel'in yerleşik özelliklerini kullanabilirsiniz.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ReportsPage;
